import { DateTime } from "luxon";
// Not the Shared/Components barrel: AppRegistry loads this file while that barrel can still be loading (circular import).
import { DurationOption, FourteenDays, SevenDays, ThirtyDays } from "Shared/Components/Filter/DurationPickerPill.types";
import { IcmIncidentSeverity } from "Enums/IcmIncidentSeverity";
import { IcmIncidentStatus } from "Enums/IcmIncidentStatus";

export const ON_CALL_LOAD_BALANCER_ROUTE = "/OnCallLoadBalancer";
export const ON_CALL_LOAD_BALANCER_TITLE = "On-Call Load Balancer";
export const ON_CALL_LOAD_BALANCER_DESCRIPTION = "Balance live-site incident pressure against current and upcoming on-call coverage across selected teams.";

export const ON_CALL_LOAD_BALANCER_STALE_TIME = 60 * 1000;
export const ON_CALL_LOAD_BALANCER_INCIDENT_PAGE_SIZE = 100;
export const ON_CALL_LOAD_BALANCER_MAX_INCIDENTS = 2000;
export const ON_CALL_LOAD_BALANCER_THIN_TEAM_MEMBER_COUNT = 3;
export const ON_CALL_LOAD_BALANCER_ENABLE_ASSIST_PANEL: boolean = false;
export const ON_CALL_LOAD_BALANCER_INCIDENT_LOOKBACK_DAYS = 7;

export const ON_CALL_LOAD_BALANCER_DEFAULT_SEVERITIES = [
    IcmIncidentSeverity.Sev0,
    IcmIncidentSeverity.Sev1,
    IcmIncidentSeverity.Sev2,
    IcmIncidentSeverity.Sev3
];

export const ON_CALL_LOAD_BALANCER_DEFAULT_STATUSES = [IcmIncidentStatus.Active, IcmIncidentStatus.Mitigated, IcmIncidentStatus.Resolved];

export const ON_CALL_LOAD_BALANCER_SEVERITY_OPTIONS: OptionItem[] = [
    { key: String(IcmIncidentSeverity.Sev0), label: "Sev0", value: IcmIncidentSeverity.Sev0 },
    { key: String(IcmIncidentSeverity.Sev1), label: "Sev1", value: IcmIncidentSeverity.Sev1 },
    { key: String(IcmIncidentSeverity.Sev2), label: "Sev2", value: IcmIncidentSeverity.Sev2 },
    { key: String(IcmIncidentSeverity.Sev3), label: "Sev3", value: IcmIncidentSeverity.Sev3 },
    { key: String(IcmIncidentSeverity.Sev4), label: "Sev4", value: IcmIncidentSeverity.Sev4 },
    { key: String(IcmIncidentSeverity.Sev25), label: "Sev25", value: IcmIncidentSeverity.Sev25 }
];

export const ON_CALL_LOAD_BALANCER_STATUS_OPTIONS: OptionItem[] = [
    { key: String(IcmIncidentStatus.Active), label: "Active", value: IcmIncidentStatus.Active },
    { key: String(IcmIncidentStatus.Mitigated), label: "Mitigated", value: IcmIncidentStatus.Mitigated },
    { key: String(IcmIncidentStatus.Resolved), label: "Resolved", value: IcmIncidentStatus.Resolved }
];

export const ON_CALL_LOAD_BALANCER_TEAM_RESULT_LIMIT = 100;
export const ON_CALL_LOAD_BALANCER_TEAM_SEARCH_MIN_LENGTH = 3;

export const ON_CALL_LOAD_BALANCER_DEFAULT_SCHEDULE_DURATION_KEY = "last7days";
export const ON_CALL_LOAD_BALANCER_DEFAULT_INCIDENT_DURATION_KEY = SevenDays.key;

export const ON_CALL_LOAD_BALANCER_SCHEDULE_DURATION_OPTIONS: DurationOption[] = [
    {
        key: "last24hours",
        text: "Last 24 hours",
        getDuration: () => {
            const now = DateTime.utc();
            return { startTime: now.minus({ hours: 24 }).toISO(), endTime: now.toISO() };
        }
    },
    {
        key: "last72hours",
        text: "Last 72 hours",
        getDuration: () => {
            const now = DateTime.utc();
            return { startTime: now.minus({ hours: 72 }).toISO(), endTime: now.toISO() };
        }
    },
    {
        key: ON_CALL_LOAD_BALANCER_DEFAULT_SCHEDULE_DURATION_KEY,
        text: "Last 7 days",
        getDuration: () => {
            const now = DateTime.utc();
            return { startTime: now.minus({ days: 7 }).toISO(), endTime: now.toISO() };
        }
    }
];

export const ON_CALL_LOAD_BALANCER_INCIDENT_DURATION_OPTIONS: DurationOption[] = [SevenDays, FourteenDays, ThirtyDays];

export const ON_CALL_LOAD_SCORE_WEIGHTS = {
    sev0: 10,
    sev1: 8,
    sev2: 5,
    sev3: 3,
    customerImpacting: 4,
    doubleBookedPrimary: 3,
    missingBackup: 4,
    upcomingHandoff: 2
} as const;

export const ON_CALL_LOAD_RISK_LABELS = {
    balanced: "Balanced",
    watch: "Watch",
    overloaded: "Overloaded",
    thinCoverage: "Thin coverage",
    missingData: "Missing schedule data"
} as const;

export const ON_CALL_LOAD_BALANCER_FILTER_IDS = {
    scheduleWindow: "onCallLoadBalancerScheduleWindow",
    incidentWindow: "onCallLoadBalancerIncidentWindow",
    team: "onCallLoadBalancerTeams",
    severity: "onCallLoadBalancerSeverity",
    status: "onCallLoadBalancerStatus"
} as const;

export const ON_CALL_CONTACT_SCHEDULE_TYPES = ["NoSubstitution", "SubstituteForContact"];
export const ON_CALL_CONTACT_POSITION_RANGE = [0, 5];
